/**
 * PatientDataForm Component
 *
 * Form for displaying and editing patient data extracted from DICOM files.
 * Used in the UploadDicomModal for the "Create/Match Automatic" mode.
 */

import { useEffect, useState } from 'react'
import { UserPlus } from 'lucide-react'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { DOCUMENT_TYPE_LABELS, DOCUMENT_TYPES } from '@/config/constants'
import type { PatientCreateData } from '@/lib/dicom-helpers'

interface PatientDataFormProps {
  initialData: PatientCreateData | null
  onChange: (data: PatientCreateData) => void
  disabled?: boolean
}

const DOCUMENT_TYPE_OPTIONS = Object.entries(DOCUMENT_TYPES).map(([value]) => ({
  value,
  label: `${value} - ${DOCUMENT_TYPE_LABELS[value as keyof typeof DOCUMENT_TYPE_LABELS]}`,
}))

const SEX_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
  { value: 'O', label: 'Otro' },
]

export function PatientDataForm({ initialData, onChange, disabled }: PatientDataFormProps) {
  const [data, setData] = useState<PatientCreateData>(
    initialData || {
      documentNumber: '',
      documentType: 'CC',
      firstName: '',
      middleName: '',
      lastName: '',
      secondLastName: '',
      birthDate: '',
      sex: 'O',
    }
  )

  // Sync with initialData when it changes (e.g., when DICOM is parsed)
  useEffect(() => {
    if (initialData) {
      setData(initialData)
    }
  }, [initialData])

  // Notify parent of changes
  useEffect(() => {
    onChange(data)
  }, [data, onChange])

  const updateField = <K extends keyof PatientCreateData>(
    field: K,
    value: PatientCreateData[K]
  ) => {
    setData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <div className="space-y-4 p-4 alert-info rounded-lg">
      <div className="flex items-center gap-2">
        <UserPlus className="h-4 w-4" />
        <span className="text-sm font-medium">
          Datos del Paciente (desde DICOM)
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Document Type + Number */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
            Documento de Identidad <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <Select
              value={data.documentType}
              onChange={(e) => updateField('documentType', e.target.value as any)}
              options={DOCUMENT_TYPE_OPTIONS}
              className="w-40"
              disabled={disabled}
            />
            <Input
              value={data.documentNumber}
              onChange={(e) => updateField('documentNumber', e.target.value)}
              placeholder="Numero de documento"
              className="flex-1"
              disabled={disabled}
            />
          </div>
        </div>

        {/* First Name */}
        <div>
          <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
            Nombre
          </label>
          <Input
            value={data.firstName || ''}
            onChange={(e) => updateField('firstName', e.target.value)}
            placeholder="Nombre del paciente"
            disabled={disabled}
          />
        </div>

        {/* Middle Name */}
        <div>
          <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
            Segundo Nombre
          </label>
          <Input
            value={data.middleName || ''}
            onChange={(e) => updateField('middleName', e.target.value)}
            placeholder="Segundo nombre"
            disabled={disabled}
          />
        </div>

        {/* Last Name */}
        <div>
          <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
            Primer Apellido <span className="text-red-500">*</span>
          </label>
          <Input
            value={data.lastName || ''}
            onChange={(e) => updateField('lastName', e.target.value)}
            placeholder="Apellido del paciente"
            disabled={disabled}
          />
        </div>

        {/* Second Last Name */}
        <div>
          <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
            Segundo Apellido
          </label>
          <Input
            value={data.secondLastName || ''}
            onChange={(e) => updateField('secondLastName', e.target.value)}
            placeholder="Segundo apellido"
            disabled={disabled}
          />
        </div>

        {/* Birth Date */}
        <div>
          <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
            Fecha de Nacimiento
          </label>
          <Input
            type="date"
            value={data.birthDate || ''}
            onChange={(e) => updateField('birthDate', e.target.value)}
            disabled={disabled}
          />
        </div>

        {/* Sex */}
        <div>
          <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
            Sexo
          </label>
          <Select
            value={data.sex || 'O'}
            onChange={(e) => updateField('sex', e.target.value as 'M' | 'F' | 'O')}
            options={SEX_OPTIONS}
            disabled={disabled}
          />
        </div>
      </div>

      <p className="text-xs text-blue-600 dark:text-blue-400">
        El sistema buscara un paciente existente con estos datos.
        Si no existe, se creara automaticamente.
      </p>
    </div>
  )
}
